<template>
  <div>
    <div class="form-group">
      <QuestionInfo v-if="!canUseGoogleSlidesPromote" />
      <label class="label-option">{{ $i18nPresenter.t("Google Slides' Published link") }}
        <span v-if="canUseGoogleSlidesPromote" class="pl-1 title-table-presentation-tooltip">
          <b-button
            v-tooltip="questionInfoTooltip"
            class="tooltip-btn"
          />
          <i class="fas fa-question-circle" />
        </span>
      </label>
      <InputText
        :val="slideActive.publishedLink"
        @interface="_setPayloadUpdateSlide"
        :place-holder="
          'E.g., https://docs.google.com/presentation/d/e/2PACX-1vTKHimi-dsAdPvA7S8ic5aZf0MhMqmu3bgHYMYKdV7n7hSbFL5TjVsPnAQnarlweEEfUHq5QGAfiDZz/pub?start=true&loop=false&delayms=3000'
        "
        spellcheck="false"
        :id-name="'google-slides-published-link'"
        :index="''"
        :limit="maxLengthPublishedLink"
        :maxlength="maxLengthPublishedLink"
      />
    </div>
    <div class="form-group">
      <div class="setting-block">
        <div class="block-content">
          <div class="form-check form-check-inline">
            <div class="custom-control custom-checkbox">
              <input
                type="checkbox"
                class="custom-control-input"
                id="google-slides-check-show-iframe"
                v-model="slideActive.showIframe"
                @change="_setPayloadUpdateSlide('showIframe')"
              >
              <label
                class="custom-control-label pointer"
                for="google-slides-check-show-iframe"
              >{{ $i18nPresenter.t('Also show on audience\'s smartphones') }}</label>
              <span class="pl-1 title-table-presentation-tooltip">
                <b-button
                  v-tooltip="showOnAudienceTooltip"
                  class="tooltip-btn"
                />
                <i class="fas fa-question-circle" />
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="form-group">
      <label class="label-option">{{ $i18nPresenter.t('Slide name (optional)') }}
        <span class="title-table-presentation-tooltip">
          <b-button
            v-tooltip="slideNameTooltip"
            class="tooltip-btn"
          />
          <i class="fas fa-question-circle" />
        </span>
      </label>
      <InputText
        :val="slideActive.title"
        @interface="_setPayloadUpdateSlide"
        :place-holder="$i18nPresenter.t('Slide name…')"
        spellcheck="false"
        :id-name="'google-slides-title'"
        :index="''"
        :limit="maxLengthTitle"
        :inputId="`slide-title-${slideActive.id}`"
        :maxlength="maxLengthTitle"
      />
    </div>
    <GoogleSlidesInfoBox v-if="canUseGoogleSlidesPromote" />
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import Util from '@/util'
import QuestionInfo from '@/components/presentations/QuestionInfo'
import InputText from '@/components/presentations/InputText'
import GoogleSlidesInfoBox from './GoogleSlidesInfoBox'
import slideApi from '@/api/slide'
import Socket from '@/socket'

export default {
  name: 'GoogleSlidesContentTab',
  components: {
    QuestionInfo,
    InputText,
    GoogleSlidesInfoBox,
  },
  data() {
    return {
      maxLengthPublishedLink: 500,
      maxLengthTitle: 80,
      gaText: '',
    }
  },
  computed: {
    ...mapState({
      currentUser: (state) => state.currentUser,
      slideActive: (state) => state.slideActive,
    }),
    ...mapGetters('featureToggles', [
      'canUseGoogleSlidesPromote',
    ]),
    showOnAudienceTooltip() {
      return this.$i18nPresenter.t('Please note that when you change slides within Google Slides, your audience will not be moved along')
    },
    slideNameTooltip() {
      return this.$i18nPresenter.t("This is to help you to identify this slide among other slides in the presentation. It won't be visible to your audience.")
    },
    questionInfoTooltip() {
      return this.$i18nPresenter.t('questionInfoInstruction')
    },
  },
  methods: {
    _setPayloadUpdateSlide(data) {
      const payload = {}
      const { slideActive } = this.$store.state
      if (data.idName === 'google-slides-published-link') {
        if (data.value === '' || (data.value !== '' && !Util.isGoogleSlidesLink(data.value))) {
          payload.publishedLink = ''
        } else if (data.value !== '') {
          if (!Util.isURL(data.value) || !Util.isGoogleSlidesLink(data.value)) return
          payload.publishedLink = this._makeGoogleSlidesEmbedLink(data.value)
        }
      } else if (data === 'showIframe') {
        payload.showIframe = slideActive.showIframe
        // GA track for showIframe
        if (payload.showIframe) {
          this.gaText = 'shows'
        } else {
          this.gaText = 'hides'
        }
        Util.mixpanelTracking(
          'Slide',
          'Edit setting',
          `Presenter ${this.gaText} Google slides iframe`,
        )
      } else if (data.idName === 'google-slides-title') {
        if (data.value) {
          payload.title = data.value
          payload.titleHTML = data.value
        } else if (data.value === '') {
          payload.title = ' '
          payload.titleHTML = ' '
        }
      }
      payload.accessCode = this.$store.state.presentation.uniqueAccessCode
      payload.socketId = Socket.getSocketId()
      this.updateSlide(payload)
    },
    _makeGoogleSlidesEmbedLink(value) {
      const pubIndex = value.indexOf('/pub?')
      if (pubIndex !== -1) {
        value = `${value.replace('/pub?', '/embed?')}`
      }
      return value
    },
    async updateSlide(payload) {
      const { slides } = this.$store.state.presentation
      const { slideActive } = this.$store.state
      this.$store.dispatch('setSyncPresentation', 1)
      try {
        await slideApi.updateSlideById(slideActive.id, payload)
        this.$store.dispatch('setSyncPresentation', 2)

        // Update presentation slides
        const index = slides.findIndex(
          (item) => {
            return item.id === slideActive.id
          },
        )
        if (index === -1) return

        if (payload.publishedLink || payload.publishedLink === '') {
          slides[index].publishedLink = payload.publishedLink
          slideActive.publishedLink = payload.publishedLink
        }
        if (payload.title) {
          slides[index].title = payload.title
        }
      } catch (error) {
        this.$store.dispatch('setSyncPresentation', 3)
        throw error
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variable.scss";

.divider {
  width: 100%;
  height: 1px;
  background-color: #DDDDDD;
  margin: -6px 0 10px 0;
}
</style>
